"use client";

import { BookmarkIcon, BookmarkFilledIcon } from "@radix-ui/react-icons";
import { Flex, Text } from "@radix-ui/themes";
import classNames from "classnames";
import React, { useState, useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import useCustomToast from "@/lib/hooks/useCustomToast";
import { useSWRConfig } from "swr";
import LoginPopup from "@/app/components/LoginPopup";
import LotLogInBanner from "@/app/components/LotLogInBanner";

interface Props {
  disabled: boolean;
  follows: number;
  following: boolean; // Is the current user following the auction?
  lotId: number;
  auctionId: number;
  userId: string | null;
  lotTitle?: string;
  lotEndDate?: Date;
}

const FollowCounter = ({
  disabled,
  follows,
  following,
  lotId,
  auctionId,
  userId,
  lotTitle,
  lotEndDate,
}: Props) => {
  const { toast, toastError } = useCustomToast();
  const { mutate } = useSWRConfig();
  const [displayLoginPopup, setDisplayLoginPopup] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  
  // Local state for immediate UI updates
  const [localFollows, setLocalFollows] = useState(follows);
  const [localFollowing, setLocalFollowing] = useState(following);

  // Sync local state with props when they change
  useEffect(() => {
    setLocalFollows(follows);
  }, [follows]);

  useEffect(() => {
    setLocalFollowing(following);
  }, [following]);

  // Block clicks when dialog is closing
  useEffect(() => {
    if (!isClosing) return;

    const blockClick = (e: MouseEvent) => {
      // Block all clicks for a brief moment after dialog closes
      e.stopPropagation();
      e.preventDefault();
      e.stopImmediatePropagation();
    };

    // Use capture phase to intercept events early
    document.addEventListener('click', blockClick, true);
    document.addEventListener('mousedown', blockClick, true);
    document.addEventListener('pointerdown', blockClick, true);

    const timer = setTimeout(() => {
      setIsClosing(false);
      document.removeEventListener('click', blockClick, true);
      document.removeEventListener('mousedown', blockClick, true);
      document.removeEventListener('pointerdown', blockClick, true);
    }, 300);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', blockClick, true);
      document.removeEventListener('mousedown', blockClick, true);
      document.removeEventListener('pointerdown', blockClick, true);
    };
  }, [isClosing]);

  const toggleSubscription = async () => {
    if (!userId) return;
    
    const newSubscriptionState = !localFollowing;
    
    // Immediate UI update
    setLocalFollowing(newSubscriptionState);
    setLocalFollows(prev => newSubscriptionState ? prev + 1 : prev - 1);

    // Async backend update
    try {
      const response = await fetch(
        `/api/account/subscriptions/subscribe`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subscribe: newSubscriptionState,
            userId: userId,
            lotId: lotId,
            auctionId: auctionId,
          }),
        }
      );

      if (!response.ok) throw new Error("Could not update subscription");

      // Update global SWR state in background
      mutate(`/api/auctions/${auctionId}/gridData`);
      mutate(`/api/account/subscriptions/${userId}/${lotId}`);

      toast({
        title: "Success!",
        description: newSubscriptionState
          ? "You are now watching this lot."
          : "You are no longer watching this lot.",
        duration: 3000,
      });
    } catch (error) {
      // Revert local state on error
      setLocalFollowing(!newSubscriptionState);
      setLocalFollows(prev => !newSubscriptionState ? prev + 1 : prev - 1);
      
      toastError({
        title: "Error",
        description: newSubscriptionState
          ? "Failed to watch this lot."
          : "Failed to unwatch this lot.",
      });
      console.error("Failed to toggle subscription:", error);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (disabled || isClosing) return;
    e.preventDefault();
    e.stopPropagation();
    if (userId) {
      toggleSubscription();
    }
  };

  const counterDisplay = (onClickHandler?: (e: React.MouseEvent) => void) => (
    <div ref={wrapperRef}>
      <Flex
        justify="center"
        align="center"
        className={classNames(
          "absolute h-7 z-30 rounded-bl-lg top-0 right-0",
          "bg-zinc-100 px-2",
          localFollowing ? "bg-opacity-40" : "bg-opacity-60",
          (userId || !onClickHandler) && "cursor-pointer hover:bg-opacity-80 transition-all duration-200"
        )}
        style={{
          backdropFilter: "blur(15px)",
        }}
        gap="1"
        onClick={onClickHandler}
      >
        <Flex>
          {localFollowing ? (
            <BookmarkFilledIcon className="h-12" />
          ) : (
            <BookmarkIcon className="h-12" />
          )}
        </Flex>
        <Text className="text-sm">{localFollows}</Text>
      </Flex>
    </div>
  );

  if (!userId) {
    return (
      <LoginPopup
        isOpen={displayLoginPopup}
        setIsOpen={(open) => {
          if (!open && displayLoginPopup) {
            // Dialog is closing
            setIsClosing(true);
          }
          setDisplayLoginPopup(open);
        }}
        onDismiss={() => {
          setIsClosing(true);
          setDisplayLoginPopup(false);
        }}
        trigger={counterDisplay((e) => {
          e.preventDefault();
          e.stopPropagation();
          setDisplayLoginPopup(true);
        })}
      >
        <LotLogInBanner
          lotId={lotId}
          auctionId={auctionId}
          message={""}
          fallbackMessage={""}
        />
      </LoginPopup>
    );
  }

  return counterDisplay(handleClick);
};

export default FollowCounter;
